import type { TransitionRootProps, DialogProps } from '@headlessui/react';
import { Dialog, DialogPanel, DialogTitle, DialogBackdrop } from '@headlessui/react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseButton from './CloseButton';
import Button, { type ButtonVariant, type ButtonIntent } from '@/components/common/Button';
import { useZIndex } from '@/utils/zIndexManager';

export interface ModalProps {
    // should the modal be shown or not
    open: boolean;
    // function to be called when the modal should be closed
    onClose: () => void;
    // title of the modal
    title?: string;
    titleRight?: ReactNode;
    // subtitle of the modal (optional text below title)
    subtitle?: string;
    // content of the modal
    children: ReactNode;
    // text for the ok button
    okText?: string;
    // variant for the ok button (defaults to 'primary')
    okVariant?: ButtonVariant;
    // intent for the ok button (defaults to 'default'; use 'destructive' for delete/danger actions)
    okIntent?: ButtonIntent;
    // should the ok button be disabled
    okDisabled?: boolean;
    // text for the cancel button
    cancelText?: string;
    // function to be called when the ok button is clicked
    onOk?: (e: React.MouseEvent<HTMLButtonElement> | KeyboardEvent) => void;
    // function to be called when the cancel button is clicked
    onCancel?: (e: React.MouseEvent<HTMLButtonElement> | KeyboardEvent) => void;
    // additional class name for the modal
    className?: string;
    // should the modal have no action
    noAction?: boolean;
    // data-cy attribute for testing
    dataCy?: string;
    // footer of the modal
    footer?: ReactNode;
    // transition props
    onAfterLeave?: TransitionRootProps['afterLeave'];
    showCloseButton?: boolean;
    largeTitle?: boolean;
    /**
     * Custom class name for the subtitle
     */
    subtitleClassName?: string;
    /**
     * Caps the modal height to the viewport and scrolls the body internally.
     * Title and footer remain pinned. Disabled by default so existing modals
     * (which may contain popovers/dropdowns) are not clipped.
     */
    scrollableBody?: boolean;
}

const Modal = ({
    open,
    onClose,
    title,
    titleRight,
    subtitle,
    children,
    okText,
    okVariant = 'primary',
    okIntent = 'default',
    okDisabled,
    cancelText,
    onOk,
    onCancel,
    className,
    noAction = false,
    dataCy,
    footer,
    onAfterLeave,
    showCloseButton = true,
    largeTitle = false,
    subtitleClassName,
    scrollableBody = false,
}: ModalProps) => {
    const { t } = useTranslation('common');

    const zIndex = useZIndex(open);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (open && event.key === 'Enter' && !(event.shiftKey || event.ctrlKey || event.altKey || event.metaKey)) {
                onOk?.(event);
                event.preventDefault();
            }
        };

        document.addEventListener('keypress', handleKeyPress);

        return () => {
            document.removeEventListener('keypress', handleKeyPress);
        };
    }, [open, onOk]);

    const onTransitionEnd = useCallback<NonNullable<DialogProps['onTransitionEnd']>>(() => {
        if (!open && onAfterLeave) {
            onAfterLeave();
        }
    }, [onAfterLeave, open]);

    // Scroll affordance: when the scrollable body overflows, fade the bottom of the
    // visible scroll area into white so the cut-off content reads as scrollable.
    // The gradient hides itself when the content fits OR the user reaches the bottom.
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [atBottom, setAtBottom] = useState(false);

    const recomputeAtBottom = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }
        const hasOverflow = container.scrollHeight > container.clientHeight + 1;
        if (!hasOverflow) {
            setAtBottom(true);
            return;
        }
        const reachedBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 2;
        setAtBottom(reachedBottom);
    }, []);

    useEffect(() => {
        if (!scrollableBody || !open) {
            return;
        }
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        recomputeAtBottom();

        const resizeObserver = new ResizeObserver(recomputeAtBottom);
        resizeObserver.observe(container);
        // Also watch children — handles late layout shifts (e.g., iframe loading).
        Array.from(container.children).forEach(child => {
            if (child instanceof Element) {
                resizeObserver.observe(child);
            }
        });

        return () => resizeObserver.disconnect();
    }, [scrollableBody, open, recomputeAtBottom]);

    return (
        <Dialog
            className="relative"
            data-cy={dataCy}
            onClose={onClose}
            onTransitionEnd={onTransitionEnd}
            open={open}
            style={{ zIndex }}
        >
            <DialogBackdrop
                className={clsx(
                    'fixed inset-0 bg-black/40',
                    // backdrop transition
                    'transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in'
                )}
                transition
            />

            {/* Modal container to place it */}
            <div className="fixed inset-0 w-screen overflow-y-auto" style={{ zIndex }}>
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                    {/* Modal */}
                    <DialogPanel
                        className={clsx(
                            // modal panel style
                            'bg-neutral-0 relative w-full transform rounded-[10px] p-5 text-left shadow-sm sm:my-8 sm:w-auto',
                            // modal panel transition
                            'transition-all data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in',
                            scrollableBody && 'flex max-h-[calc(100vh-4rem)] flex-col',
                            className
                        )}
                        transition
                    >
                        {title && (
                            <div className={clsx('mt-0')}>
                                <div className="flex items-baseline justify-between">
                                    <div className="flex flex-col gap-1">
                                        <DialogTitle
                                            as="h3"
                                            className={clsx(
                                                'break-words',
                                                largeTitle
                                                    ? 'text-xl leading-8 font-bold text-neutral-900 lg:text-2xl'
                                                    : 'text-base leading-6 font-semibold text-neutral-900'
                                            )}
                                        >
                                            {title}
                                        </DialogTitle>
                                        {subtitle && (
                                            <p
                                                className={clsx(
                                                    'text-muted-foreground leading-6 font-medium',
                                                    subtitleClassName
                                                )}
                                            >
                                                {subtitle}
                                            </p>
                                        )}
                                    </div>
                                    {titleRight && <div>{titleRight}</div>}
                                    {showCloseButton && (
                                        <div className="relative h-6 w-6 overflow-hidden">
                                            <CloseButton onClick={onClose} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {scrollableBody ? (
                            <div className="relative mt-5 min-h-0 flex-1">
                                <div
                                    ref={scrollContainerRef}
                                    className="size-full overflow-y-auto"
                                    onScroll={recomputeAtBottom}
                                >
                                    {children}
                                </div>
                                <div
                                    aria-hidden
                                    className={clsx(
                                        'to-background/80 pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-linear-to-b from-transparent transition-opacity duration-150',
                                        atBottom ? 'opacity-0' : 'opacity-100'
                                    )}
                                />
                            </div>
                        ) : (
                            <div className="mt-5">{children}</div>
                        )}
                        {footer && <div className={clsx('mt-5', scrollableBody && 'shrink-0')}>{footer}</div>}
                        {!noAction && !footer && (
                            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <Button
                                    className="ml-3"
                                    variant={okVariant}
                                    intent={okIntent}
                                    disabled={okDisabled}
                                    onClick={onOk}
                                >
                                    {okText || t('common:modal.ok')}
                                </Button>
                                <Button className="ml-3" variant="secondary" onClick={onCancel || onClose}>
                                    {cancelText || t('common:modal.cancel')}
                                </Button>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};

export default Modal;
